import { Alert, Input, Modal, Select, Switch } from 'antd';
import Section from '../console/Section.jsx';

// Match keys + reconciliation switches. `patch(partial)` shallow-merges into the config. Switch rows
// carry their consequence as a description line; close-missing wears the reserved warm "live" tone
// and is confirm-gated at toggle time AND at save time — it is the only setting here that can close
// real jobs. Close-missing closes EVERYTHING absent from the feed by default; `closeSource` is an
// opt-in narrowing, so the confirm sits on the toggle that *widens* the scope.
function SwitchRow({ label, description, checked, onChange, live }) {
	return (
		<div className="js-switchrow">
			<Switch checked={!!checked} onChange={onChange} className={live ? 'js-switch-live' : undefined} />
			<div>
				<div className="js-switchrow-label">{label}</div>
				{description ? <div className="js-switchrow-desc">{description}</div> : null}
			</div>
		</div>
	);
}

const hasKey = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

export default function ReconciliationSection({ config, patch }) {
	const match = config.match || { incomingKey: '', erinKeys: [] };
	const rec = config.reconciliation || {};
	const defaultSource = (config.defaults && config.defaults.externalSource) || '';
	// The restriction is "on" when the key is present, even if the value is still empty — otherwise
	// a half-typed '' would silently read as "close everything". validateConfig blocks that state.
	const restricted = hasKey(rec, 'closeSource');

	const patchMatch = (partial) => patch({ match: { ...match, ...partial } });
	const patchRec = (partial) => patch({ reconciliation: { ...rec, ...partial } });
	const dropRecKey = (key) => {
		const next = { ...rec };
		delete next[key];
		patch({ reconciliation: next });
	};

	// Turning close-missing on is destructive, and the default scope is every job.
	const onToggleCloseMissing = (on) => {
		if (!on) {
			patchRec({ closeMissing: false });
			return;
		}
		Modal.confirm({
			title: 'Close missing jobs on each sync?',
			content: restricted ? (
				<span>
					On every live run, open jobs absent from the feed will be closed — only those whose external
					source is <code>{rec.closeSource || '(not set)'}</code>. Everything else is left alone.
				</span>
			) : (
				<span>
					On every live run, <b>every</b> open job absent from the feed will be closed, including jobs
					from other ATSs and ones created by hand in ERIN. Restrict it to a single source below if that
					is not what you want.
				</span>
			),
			okText: 'Enable',
			okButtonProps: { danger: true },
			onOk: () => patchRec({ closeMissing: true }),
		});
	};

	// Restricting narrows the blast radius, so it needs no confirm. Lifting the restriction widens it
	// back to every job on the company — that is the destructive direction, so gate that one.
	const onToggleRestrict = (on) => {
		if (on) {
			patchRec({ closeSource: defaultSource });
			return;
		}
		if (!rec.closeMissing) {
			dropRecKey('closeSource');
			return;
		}
		Modal.confirm({
			title: 'Close jobs from every source?',
			content: (
				<span>
					Without a source restriction, <b>every</b> open job absent from the feed closes on each live
					run — other ATSs&apos; jobs, legacy rows, and ones created by hand in ERIN.
				</span>
			),
			okText: 'Remove restriction',
			okButtonProps: { danger: true },
			onOk: () => dropRecKey('closeSource'),
		});
	};

	const onNormalizeChange = (vals) => {
		if (vals.length) {
			patchMatch({ normalize: vals });
		} else {
			// omit `normalize` entirely when empty rather than storing []
			const next = { ...match };
			delete next.normalize;
			patch({ match: next });
		}
	};

	return (
		<Section
			id="sec-match"
			title="Match & reconciliation"
			description="How incoming records are matched to existing ERIN jobs, and what happens on each sync."
		>
			<div className="ei-fgrid">
				<div className="ei-fitem is-narrow">
					<span className="ei-flabel">Incoming key</span>
					<div className="js-token">
						<Input
							value={match.incomingKey}
							onChange={(e) => patchMatch({ incomingKey: e.target.value })}
							placeholder="e.g. externalJobId"
						/>
					</div>
				</div>
				<div className="ei-fitem">
					<span className="ei-flabel">ERIN match keys</span>
					<div className="js-token js-tags-accent">
						<Select
							mode="tags"
							value={Array.isArray(match.erinKeys) ? match.erinKeys : []}
							onChange={(vals) => patchMatch({ erinKeys: vals })}
							tokenSeparators={[',']}
							placeholder="externalJobId, fullExternalJobId"
							style={{ width: '100%' }}
							open={false}
						/>
					</div>
				</div>
				<div className="ei-fitem is-narrow">
					<span className="ei-flabel">Normalize</span>
					<div className="js-token">
						<Select
							mode="multiple"
							value={Array.isArray(match.normalize) ? match.normalize : []}
							onChange={onNormalizeChange}
							options={[
								{ value: 'trim', label: 'trim' },
								{ value: 'lower', label: 'lower' },
							]}
							placeholder="none"
							style={{ width: '100%' }}
						/>
					</div>
				</div>
			</div>

			<div className="ei-minilabel has-rule">On each sync</div>
			<div className="js-switchlist">
				<SwitchRow
					label="Create new jobs"
					description="Add records from the feed that don't exist in ERIN yet."
					checked={rec.create}
					onChange={(v) => patchRec({ create: v })}
				/>
				<SwitchRow
					label="Update changed jobs"
					description="Overwrite existing jobs when the source has changed."
					checked={rec.update}
					onChange={(v) => patchRec({ update: v })}
				/>
				<SwitchRow
					label="Skip unchanged"
					description="Leave records untouched when nothing has changed. Compared on the change-tracked fields: title, description, jobType, status, publicLink, externalSource."
					checked={rec.updateSkipUnchanged}
					onChange={(v) => patchRec({ updateSkipUnchanged: v })}
				/>
				<SwitchRow
					label="Close missing jobs"
					description={
						restricted ? (
							<>
								Close open jobs absent from the feed — only where{' '}
								<span className="js-mono">externalSource = {rec.closeSource || '…'}</span>.
							</>
						) : (
							<>Close every open job absent from the feed, whatever created it.</>
						)
					}
					checked={rec.closeMissing}
					onChange={onToggleCloseMissing}
					live
				/>
				{rec.closeMissing && (
					<SwitchRow
						label="Only close jobs from one source"
						description="Narrow close-missing to a single externalSource. Leaves other ATSs, legacy rows, and hand-created jobs open."
						checked={restricted}
						onChange={onToggleRestrict}
						live
					/>
				)}
			</div>

			{rec.closeMissing && (
				<>
					<Alert
						style={{ marginTop: 14 }}
						type={restricted ? 'warning' : 'error'}
						showIcon
						message={
							restricted ? (
								<>
									Closes only <span className="js-mono">{rec.closeSource || '(not set)'}</span> jobs.
									Other ATSs, legacy rows, and manually-created jobs stay open.
								</>
							) : (
								<>
									Closes <b>every</b> open job absent from the feed on each live run — including other
									ATSs&apos; jobs and manually-created ones. A short or truncated feed closes the rest.
								</>
							)
						}
					/>
					{restricted && (
						<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
							<span className="ei-minilabel" style={{ margin: 0 }}>
								externalSource =
							</span>
							<div className="js-token" style={{ maxWidth: 220 }}>
								<Input
									value={rec.closeSource == null ? '' : rec.closeSource}
									onChange={(e) => patchRec({ closeSource: e.target.value })}
									placeholder={defaultSource || 'e.g. CLAUS'}
									status={String(rec.closeSource || '').trim() ? undefined : 'error'}
								/>
							</div>
							<span className="js-switchrow-desc">Required while the restriction is on.</span>
						</div>
					)}
				</>
			)}
		</Section>
	);
}
